import { useCallback, useEffect, useMemo, useState } from 'react';
import { ITEM_REPAIR } from '@/consts/bodyAndPaint';
import { RepairItemSelectInfoType, RepairItemType } from '@/types/bodyAndPaint';

interface RepairItemSelectStateHookProps {
  initial: RepairItemSelectInfoType;
  insuranceUse: boolean;
}
export const useRepairItemSelectState = ({
  initial,
  insuranceUse,
}: RepairItemSelectStateHookProps) => {
  const [selectData, setSelectData] = useState(initial ?? []);
  useEffect(() => {
    setSelectData(initial);
  }, [initial]);

  const changeSelectItem = useCallback(
    ({ index, select }: { index: number; select: number }) => {
      setSelectData((prev) => [
        ...prev.map((prevItem, idx) =>
          idx === index
            ? {
                ...prevItem,
                repair_select: select,
              }
            : prevItem,
        ),
      ]);
    },
    [setSelectData],
  );

  const repairItemList = useMemo(() => {
    const itemList = selectData.reduce((list, item) => {
      type RepairLabel = { selectLabel: string; amountLabel: string };
      const itemKeyList: Partial<
        Record<keyof typeof ITEM_REPAIR, RepairLabel>
      > =
        insuranceUse && item.is_insurance !== undefined
          ? {
              USE_INSURANCE: {
                selectLabel: '保険を使って修理する',
                amountLabel: `${item.applied_amount.toLocaleString('ja-JP')}円`,
              },
              NO_INSURANCE: {
                selectLabel: insuranceUse
                  ? '保険を使わず、修理する'
                  : '修理する',
                amountLabel: `${item.repair_amount.toLocaleString('ja-JP')}円`,
              },
              NO_REPAIR: { selectLabel: '修理しない', amountLabel: '----' },
            }
          : {
              NO_INSURANCE: {
                selectLabel: '修理する',
                amountLabel: `${item.repair_amount.toLocaleString('ja-JP')}円`,
              },
              NO_REPAIR: { selectLabel: '修理しない', amountLabel: '' },
            };

      const { selectList, amountList } = Object.values(itemKeyList).reduce(
        ({ selectList, amountList }, item) => {
          selectList.push(item.selectLabel);
          amountList.push(item.amountLabel);
          return { selectList, amountList };
        },
        { selectList: [] as string[], amountList: [] as string[] },
      );

      const repairItem: RepairItemType = {
        repairItemName: item.repair_item_name,
        selectValue:
          item.repair_select !== undefined && item.repair_select >= 0
            ? Object.values(itemKeyList).map((item) => item.selectLabel)[
                item.repair_select
              ]
            : '',
        isInsurance: insuranceUse ? item.is_insurance : undefined,
        selectList,
        amountList,
      };
      list.push(repairItem);
      return list;
    }, [] as RepairItemType[]);
    return itemList;
  }, [selectData, insuranceUse]);

  const totalAmount: number = useMemo(() => {
    const amount = selectData.reduce((calc, item) => {
      const itemKeyList: Partial<Record<keyof typeof ITEM_REPAIR, number>> =
        insuranceUse && item.is_insurance !== undefined
          ? {
              USE_INSURANCE: item.applied_amount,
              NO_INSURANCE: item.repair_amount,
              NO_REPAIR: 0,
            }
          : {
              NO_INSURANCE: item.repair_amount,
              NO_REPAIR: 0,
            };

      const add =
        item.repair_select !== undefined && item.repair_select >= 0
          ? Object.values(itemKeyList).map((amount) => amount)[
              item.repair_select
            ] ?? 0
          : 0;
      const newValue = calc + add;
      return newValue;
    }, 0);
    return amount > 0 ? amount : 0;
  }, [selectData, insuranceUse]);

  return {
    selectData,
    repairItemList,
    changeSelectItem,
    totalAmount,
  };
};
